import { ReplyIcon } from "lucide-react";
import { toast } from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";

import classNames from "components/ui/classNames";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormMessage } from "components/ui/Form";
import type { ISelfieQuery } from "../../../lib/queries/__generated__/SelfieQuery.generated";
import Avatar from "../../core/Avatar";
import CommentInputTextArea from "../CommentInputTextArea";

import type { CommentInputFormData } from "./shapes/commentInputFormDataShape";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface RootCommentInputFormProps {
  viewer: NonNullable<ISelfieQuery["selfie"]>;
  bodyAsPlainText?: string;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function RootCommentInputForm({ viewer, bodyAsPlainText = "", onSubmit }: RootCommentInputFormProps) {
  const intl = useIntl();
  const form = useForm<CommentInputFormData>({
    mode: "onSubmit",
    resolver: zodResolver(commentInputFormDataShape),
    defaultValues: {
      bodyAsPlainText,
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const handleSubmit = async ({ bodyAsPlainText }: CommentInputFormData) => {
    try {
      await onSubmit(bodyAsPlainText);

      setTimeout(() => {
        form.reset();
      }, 1);
    } catch (err) {
      toast.error(translateServerErrorIntoFriendlyMessage(err, intl));
    }
  };

  return (
    <div className="space-y-2 p-6">
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-[42px_1fr]">
        <Avatar
          src={viewer.pictureUrl}
          hashValue={viewer._id}
          alt={viewer.fullName}
          size={42}
          squircle={true}
          className="hidden sm:inline-flex"
        />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col items-start gap-x-2 space-y-2.5 lg:flex-row lg:space-y-0"
          >
            <FormField
              control={form.control}
              name="bodyAsPlainText"
              render={({ field }) => {
                return (
                  <div className="w-full space-y-2.5">
                    <CommentInputTextArea
                      {...field}
                      placeholder={intl.formatMessage({
                        id: "qr8o9T",
                        defaultMessage: "Escreva alguma coisa aqui...",
                      })}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          form.handleSubmit(handleSubmit)();
                        }
                      }}
                    />

                    <p className="text-xs font-medium italic text-slate-400 dark:text-gray-600 md:text-sm">
                      *
                      <FormattedMessage
                        defaultMessage="Os comentários relacionados a problemas no site serão apagados, favor entrar em contato com o suporte."
                        id="c8jNBZ"
                      />
                    </p>

                    {field.value && <FormMessage />}
                  </div>
                );
              }}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className={classNames(
                "hidden rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600 lg:flex",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "dark:bg-teal-600/20 dark:text-teal-200",
                "dark:disabled:bg-slate-600/20 dark:disabled:text-slate-500",
              )}
            >
              <ReplyIcon strokeWidth={2} />
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className={classNames(
                "block w-full rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600 sm:w-auto lg:hidden",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "dark:bg-teal-600/20 dark:text-teal-200",
                "disabled:opacity-60",
                "dark:disabled:bg-slate-600/20 dark:disabled:text-slate-500",
              )}
            >
              <span className="inline-flex w-full justify-center text-sm font-medium">
                <FormattedMessage defaultMessage="Enviar comentário" id="+DJyJ7" />
              </span>
            </button>
          </form>
        </Form>
      </div>
    </div>
  );
}
